import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { apiForm, apiJson } from "@/data/api";

export interface WorkspaceSummary {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
  /** Days capture audio is kept. null = platform default (30). 0 = deleted right after processing. */
  audio_retention_days: AudioRetentionDays | null;
  /** Days transcripts are kept, from conversation end. null = forever. */
  transcript_retention_days: TranscriptRetentionDays | null;
  /** false = enrollment recordings are deleted once speaker setup completes. */
  retain_enrollment_audio: boolean;
  created_at: number;
  updated_at: number;
}

export type AudioRetentionDays = 0 | 1 | 7 | 30;
export type TranscriptRetentionDays = 30 | 90 | 365;

export interface WorkspaceRetentionUpdate {
  audio_retention_days?: AudioRetentionDays | null;
  transcript_retention_days?: TranscriptRetentionDays | null;
  retain_enrollment_audio?: boolean;
}

export interface MembershipSummary {
  role: string;
  created_at: number;
  /**
   * Onboarding gate signal (plan 029): an accepted voice enrollment sample
   * exists for this membership. Hosted voiceprint readiness is deliberately
   * not part of this flag.
   */
  voice_enrolled: boolean;
  workspace: WorkspaceSummary;
}

interface PendingInvite {
  id: string;
  email: string;
  organizationId: string | null;
  acceptInvitationUrl: string;
}

interface WorkspacesMeResponse {
  memberships: MembershipSummary[];
  pending_invites: PendingInvite[];
}

export interface WorkspaceMemberRow {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string;
  user_avatar_url: string | null;
  role: WorkspaceRoleSlug;
  status: "active" | "pending" | "inactive";
  created_at: string | number;
  assignable_roles: WorkspaceRoleSlug[];
  can_remove: boolean;
}

export interface WorkspaceInvitationRow {
  id: string;
  workos_invitation_id: string;
  email: string;
  role: WorkspaceRoleSlug;
  state: "pending" | "accepted" | "expired" | "revoked";
  created_at: string | number;
}

export type WorkspaceRoleSlug = "owner" | "admin" | "member";

interface WorkspaceResponse {
  workspace: WorkspaceSummary;
}

interface WorkspaceMembersResponse {
  members: WorkspaceMemberRow[];
}

interface WorkspaceInvitesResponse {
  invitations: WorkspaceInvitationRow[];
}

interface InviteMetadataResponse {
  requires_auth?: boolean;
  email_mismatch?: boolean;
  workspace?: { id: string | null; slug: string | null; name: string | null };
  invite_email?: string;
  current_email?: string;
  role?: WorkspaceRoleSlug;
}

export const workspaceKeys = {
  all: ["workspaces"] as const,
  me: () => [...workspaceKeys.all, "me"] as const,
  detail: (workspaceSlug: string | null | undefined) =>
    [...workspaceKeys.all, "detail", workspaceSlug ?? null] as const,
  members: (workspaceId: string | null | undefined) =>
    [...workspaceKeys.all, "members", workspaceId ?? null] as const,
  invites: (workspaceSlug: string | null | undefined) =>
    [...workspaceKeys.all, "invites", workspaceSlug ?? null] as const,
};

export function workspacesMeQueryOptions() {
  return queryOptions({
    queryKey: workspaceKeys.me(),
    queryFn: listMyWorkspaces,
    staleTime: 15_000,
  });
}

function useWorkspacesMe() {
  return useQuery(workspacesMeQueryOptions());
}

export function useCurrentWorkspace(workspaceSlug?: string | null) {
  const routeSlug = useRouteWorkspaceSlug();
  const currentSlug = workspaceSlug ?? routeSlug;
  const query = useWorkspacesMe();
  const memberships = query.data?.memberships ?? [];
  const currentMembership =
    memberships.find(
      (membership) => membership.workspace.slug === currentSlug,
    ) ?? null;

  return {
    loading: query.isPending,
    memberships,
    pendingInvites: query.data?.pending_invites ?? [],
    currentSlug,
    currentWorkspace: currentMembership?.workspace ?? null,
    currentMembership,
    refresh: async () => {
      await query.refetch();
    },
  };
}

export function useWorkspaceQuery(workspaceSlug: string | null | undefined) {
  return useQuery({
    queryKey: workspaceKeys.detail(workspaceSlug),
    queryFn: () => getWorkspace(workspaceSlug ?? ""),
    enabled: Boolean(workspaceSlug),
    staleTime: 15_000,
  });
}

export function useWorkspaceMembersQuery(
  workspaceId: string | null | undefined,
) {
  return useQuery({
    queryKey: workspaceKeys.members(workspaceId),
    queryFn: () => listWorkspaceMembers(workspaceId ?? ""),
    enabled: Boolean(workspaceId),
    staleTime: 15_000,
  });
}

export function useWorkspaceInvitesQuery(
  workspaceSlug: string | null | undefined,
) {
  return useQuery({
    queryKey: workspaceKeys.invites(workspaceSlug),
    queryFn: () => listWorkspaceInvites(workspaceSlug ?? ""),
    enabled: Boolean(workspaceSlug),
    staleTime: 15_000,
  });
}

export function useUpdateWorkspaceNameMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      updateWorkspaceName(workspaceSlug ?? "", name),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceSlug),
      });
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.me() });
    },
  });
}

export function useUpdateWorkspaceRetentionMutation(
  workspaceSlug: string | null,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: WorkspaceRetentionUpdate) =>
      updateWorkspaceRetention(workspaceSlug ?? "", update),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceSlug),
      });
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.me() });
    },
  });
}

export function useUploadWorkspaceLogoMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadWorkspaceLogo(workspaceSlug ?? "", file),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.detail(workspaceSlug),
      });
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.me() });
    },
  });
}

export function useCreateInviteMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: WorkspaceRoleSlug }) =>
      createWorkspaceInvite(workspaceSlug ?? "", input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.invites(workspaceSlug),
      });
    },
  });
}

export function useUpdateMemberRoleMutation(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; role: WorkspaceRoleSlug }) =>
      updateMemberRole(workspaceId ?? "", input.userId, input.role),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.members(workspaceId),
      });
    },
  });
}

export function useRemoveMemberMutation(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => removeMember(workspaceId ?? "", userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.members(workspaceId),
      });
    },
  });
}

export function useRevokeInviteMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: workspaceKeys.invites(workspaceSlug),
      });
    },
  });
}

export function useCreateWorkspaceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: workspaceKeys.me() });
    },
  });
}

export function listMyWorkspaces() {
  return apiJson<WorkspacesMeResponse>("/workspaces/me");
}

export function getWorkspace(workspaceSlug: string) {
  return apiJson<WorkspaceResponse>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}`,
  );
}

export function updateWorkspaceName(workspaceSlug: string, name: string) {
  return apiJson<WorkspaceResponse>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}`,
    {
      method: "PATCH",
      json: { name },
    },
  );
}

function updateWorkspaceRetention(
  workspaceSlug: string,
  update: WorkspaceRetentionUpdate,
) {
  return apiJson<WorkspaceResponse>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}`,
    {
      method: "PATCH",
      json: update,
    },
  );
}

export function uploadWorkspaceLogo(workspaceSlug: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  return apiForm<WorkspaceResponse>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/logo`,
    form,
  );
}

export function listWorkspaceMembers(workspaceId: string) {
  return apiJson<WorkspaceMembersResponse>(
    `/workspaces/${encodeURIComponent(workspaceId)}/members`,
  );
}

export function listWorkspaceInvites(workspaceSlug: string) {
  return apiJson<WorkspaceInvitesResponse>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/invites`,
  );
}

export function createWorkspaceInvite(
  workspaceSlug: string,
  input: { email: string; role: WorkspaceRoleSlug },
) {
  return apiJson<void>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/invites`,
    {
      method: "POST",
      json: input,
    },
  );
}

export function updateMemberRole(
  workspaceId: string,
  userId: string,
  role: WorkspaceRoleSlug,
) {
  return apiJson<void>(
    `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(
      userId,
    )}`,
    {
      method: "PATCH",
      json: { role },
    },
  );
}

export function removeMember(workspaceId: string, userId: string) {
  return apiJson<void>(
    `/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(
      userId,
    )}`,
    { method: "DELETE" },
  );
}

export function revokeInvitation(workosInvitationId: string) {
  return apiJson<void>(
    `/invitations/${encodeURIComponent(workosInvitationId)}/revoke`,
    { method: "POST" },
  );
}

export async function checkSlugAvailable(slug: string): Promise<boolean> {
  await apiJson<void>(`/workspaces/check-slug/${encodeURIComponent(slug)}`);
  return true;
}

export function createWorkspace(input: { name: string; slug: string }) {
  return apiJson<WorkspaceResponse>("/workspaces", {
    method: "POST",
    json: input,
  });
}

export function getInviteMetadata(token: string) {
  return apiJson<InviteMetadataResponse>(
    `/invite/${encodeURIComponent(token)}`,
  );
}

export function acceptInvite(token: string) {
  return apiJson<InviteMetadataResponse>(
    `/invite/${encodeURIComponent(token)}/accept`,
    { method: "POST" },
  );
}

// One-click redemption: the invite link itself signs the invitee in (the
// backend mints + redeems a Magic Auth code server-side) and accepts, all in
// one POST. Only works while the invitation is pending; 410 afterwards.
export function redeemInviteSession(token: string) {
  return apiJson<InviteMetadataResponse>(
    `/invite/${encodeURIComponent(token)}/session`,
    { method: "POST" },
  );
}

function useRouteWorkspaceSlug(): string | null {
  try {
    const params = useParams({ strict: false }) as {
      workspaceSlug?: string;
    };
    return params.workspaceSlug ?? null;
  } catch {
    return null;
  }
}
